<template>
	<div>
		<v-container v-if="dataLoading" class="content" fluid>
			<v-layout row>
				<v-flex text-center>
					<v-progress-circular indeterminate color="accent"></v-progress-circular>
				</v-flex>
			</v-layout>
		</v-container>
		<v-container v-else-if="!request && !dataLoading" class="content" fluid>
			<v-layout row>
				<v-flex text-center> Stornierungsantrag konnte nicht gefunden werden. </v-flex>
			</v-layout>
		</v-container>
		<v-container v-else class="content" fluid>
			<v-snackbar
				v-model="snackbar"
				:timeout="10000"
				:top="true"
				color="accent"
				class="black--text"
			>
				{{ snackbarText }}
				<v-btn class="blexon-button" small icon @click="snackbar = false">
					<v-icon>close</v-icon>
				</v-btn>
			</v-snackbar>
			<v-layout row ma-0>
				<v-flex shrink>
					<h1 class="display-1">Stornierungsantrag zu Auftrag {{ orderIdFromRoute }}</h1>
				</v-flex>
			</v-layout>
			<v-layout row ma-0>
				<v-flex v-if="request == null || request.length <= 0">
					Stornierungsanfrage konnte nicht gefunden werden.
				</v-flex>
				<v-flex v-else>
					<p>
						Bitte teilen Sie uns mit, welche Arbeitsschritte bei den zu stornierenden
						Positionen bereits durchgeführt wurden.<br />
						Zudem kann angegeben werden, wie viele Teile beim aktuellen Arbeitsschritt
						bereits verarbeitet wurden. Es wird angenommen, dass in allen
						Arbeitsschritten vor dem Aktuellen, alle Teile bereits verarbeitet wurden.
						Die Angabe der bereits verarbeiteten Teile hat also nur eine Auswirkung auf
						den aktuellen Arbeitsschritt.
					</p>
				</v-flex>
			</v-layout>
			<v-form ref="form" v-model="formValid">
				<v-data-table
					:headers="positionHeaders"
					:items="request.positions"
					:loading="dataLoading"
					item-key="articleId"
					hide-actions
					class="elevation-1"
				>
					<v-progress-linear
						slot="progress"
						color="accent"
						indeterminate
					></v-progress-linear>
					<template slot="items" slot-scope="props">
						<tr>
							<td class="text-xs-left">
								{{ props.item.position }}
							</td>
							<td class="text-xs-left">
								{{ props.item.articleId }}
							</td>
							<td class="text-xs-left py-1">
								<v-img
									v-bind:src="getThumbnailUrl(props.item.articleId)"
									contain
									width="60px"
									height="60px"
								></v-img>
							</td>
							<td class="text-xs-left">
								<v-slider
									:value="getValueFromCurrentStep(props.item)"
									@change="setCurrentStep($event, props.item)"
									:tick-labels="getTickLabels(props.item)"
									:min="0"
									:max="props.item.usedSteps.length - 1"
									step="1"
									ticks="always"
									tick-size="3"
									color="accent"
								></v-slider>
							</td>
							<td class="text-xs-right">
								<v-layout ma-0 row align-center>
									<v-flex>
										<v-text-field
											v-model="props.item.partsProcessed"
											label="Teile"
											type="number"
											dense
											:rules="[
												partRules.empty(),
												partRules.number(),
												partRules.integer(),
												partRules.max(props.item.quantity),
											]"
											required
										></v-text-field>
									</v-flex>
									<v-flex>
										<span class="text-body-1"
											>/&nbsp;{{ props.item.quantity }}</span
										>
									</v-flex>
								</v-layout>
							</td>
						</tr>
					</template>
					<template slot="no-data"> Bitte Liefertermin auswählen </template>
				</v-data-table>
				<v-layout row mx-0 mt-3 class="justify-end">
					<v-flex xs2 mr-2>
						<v-btn block @click="cancel">Abbrechen</v-btn>
					</v-flex>
					<v-flex xs2>
						<v-btn
							block
							class="blexon-button"
							label="Bestätigt den Stand der Artikel"
							:disabled="!formValid"
							@click="confirmProgress"
							>Bestätigen</v-btn
						>
					</v-flex>
				</v-layout>
			</v-form>
		</v-container>
	</div>
</template>

<script>
import httpClient from "../store/httpClient";

export default {
	components: {},
	data() {
		return {
			dataLoading: false,
			snackbar: false,
			snackbarText: "",
			formValid: true,
			partRules: {
				empty() {
					return (v) => v !== "" || "Muss angegeben werden.";
				},
				number() {
					return (v) => !isNaN(v) || "Bitte Zahl (ohne Komma) eingeben";
				},
				integer() {
					return (v) => v % 1 === 0 || "Bitte nur Ganzzahlen eingeben";
				},
				max(max) {
					return (v) =>
						(v >= 0 && v <= max) || `Muss zwischen 0 und ${max.toString()} liegen`;
				},
			},
			positionHeaders: [
				{
					text: "Position",
					align: "left",
					value: "position",
					sortable: false,
					width: "1%",
				},
				{
					text: "Artikel Id",
					align: "left",
					sortable: false,
					value: "articleId",
					width: "1%",
				},
				{
					text: "Bild",
					align: "left",
					sortable: false,
					value: "articleId",
					width: "1%",
				},
				{
					text: "Fortschritt",
					align: "left",
					sortable: false,
					value: "currentProcessStep",
					width: "100%",
				},
				{
					text: "Verarbeitete Teile (im letzten Arbeitsschritt)",
					align: "right",
					sortable: false,
					value: "partsProcessed",
					width: "1%",
				},
			],
		};
	},

	async mounted() {
		if (!this.request) {
			await this.loadRequest();
		}
		// Set processed parts to quantity for all positions
		// This is done for convenience because most of the time
		// all parts will be processed for a certain step.
		this.request.positions.forEach((p) => {
			p.partsProcessed = p.quantity;
		});
	},

	computed: {
		request() {
			if (this.$store.state.orderCancellationRequests) {
				return this.$store.state.orderCancellationRequests.filter(
					(o) => o.blexonOrderId.toString() === this.$route.params.orderId
				)[0];
			} else {
				return null;
			}
		},
		orderIdFromRoute() {
			return this.$route.params.orderId;
		},
		allProcessSteps() {
			return [
				{
					name: "Unbearbeitet",
					value: 0,
				},
				{
					name: "Programmiert",
					value: 1,
				},
				{
					name: "Laser/Kombi",
					value: 2,
				},
				{
					name: "Entgratet",
					value: 3,
				},
				{
					name: "Abgekantet",
					value: 4,
				},
				{
					name: "Geschweisst",
					value: 200,
				},
				{
					name: "Verzinkt",
					value: 250,
				},
				{
					name: "Pulverbeschichtet",
					value: 300,
				},
				{
					name: "Verzinkt",
					value: 400,
				},
			];
		},
	},

	methods: {
		async loadRequest() {
			try {
				this.dataLoading = true;
				var fertigerId = this.$route.params.fertigerId;
				let response = await httpClient().get(
					`/api/Production/GetOrderCancellationRequests?fertigerId=` + fertigerId
				);
				this.$store.commit("setOrderCancellationRequests", response.data);
			} catch (err) {
				console.log(err);
				this.setSnackbarText("Fehler beim Laden der Stornierungsanträge. ");
			} finally {
				this.dataLoading = false;
			}
		},
		async confirmProgress() {
			if (!this.formValid) return;
			try {
				this.dataLoading = true;
				// Don't wait for the cancellation because it might take a long time
				// if the order has to be recalculated and is "forced"
				httpClient().post(`/api/Production/ConfirmOrderProgress`, this.request);
				this.$store.commit("setOrderCancellationRequest", this.request);

				// Wait 2 seconds so that the state of the cancellation will already
				// be set to "busy" when reloading the orders/cancellations in the order view.
				await new Promise((resolve) => setTimeout(resolve, 2000));
				var fertigerId = this.$route.params.fertigerId;
				this.$router.push({ name: "order", params: { fertigerId: fertigerId } });
			} catch (err) {
				console.log(err);
				this.setSnackbarText("Fehler beim Bestätigen des Arbeitsstandes. ");
			} finally {
				this.dataLoading = false;
			}
		},
		getValueFromCurrentStep(position) {
			let currentValue = position.usedSteps.indexOf(position.currentProcessStep);
			return currentValue;
		},
		setCurrentStep(event, position) {
			let convertedValue = position.usedSteps[event];
			let data = {
				orderId: this.request.blexonOrderId,
				articleId: position.articleId,
				value: convertedValue,
			};
			this.$store.commit("setProcessStep", data);
		},
		getTickLabels(position) {
			return position.usedSteps.map(
				(step) => this.allProcessSteps.find((s) => s.value === step).name
			);
		},
		getThumbnailUrl(articleId) {
			return process.env.VUE_APP_erpUrl + `article/GetThumbnail?id=${articleId}`;
		},
		cancel() {
			this.$router.back();
		},
		setSnackbarText(text) {
			this.snackbarText = text;
			this.snackbar = true;
		},
	},
};
</script>

<style>
.v-slider__ticks {
	font-size: 13px;
}
</style>
